// Client half of model-generated chat titles (server: src/thread-title.mjs).
//
// On a conversation's opening turn the server splices one transient data part
// into the turn's UI-message stream — `data-ponevaThreadTitle` with the thread
// id and the model-written title. It is lifted off the stream HERE, at the
// transport, before the chat runtime ever sees it: the runtime keeps working
// with the exact chunk vocabulary it did before, and the title lands in the
// server thread metadata through the callback instead.
// Resumed streams (the reconnect path) carry the part too, so a title survives
// a mid-turn network drop the same way the answer does.

import type { UIMessage, UIMessageChunk } from 'ai';
import type { createResumeCancellation } from './resume-cancellation';
import { AssistantChatTransport } from '@assistant-ui/react-ai-sdk';

export const THREAD_TITLE_CHUNK_TYPE = 'data-ponevaThreadTitle';

export interface ThreadTitleEvent {
  threadId: string;
  title: string;
}

interface ThreadTitleChunk {
  type: typeof THREAD_TITLE_CHUNK_TYPE;
  data: ThreadTitleEvent;
}

/** Structural check: the chunk is ours and carries a non-empty title. */
export function threadTitleEvent(chunk: unknown): ThreadTitleEvent | null {
  if (!chunk || typeof chunk !== 'object') return null;
  const candidate = chunk as Partial<ThreadTitleChunk>;
  if (candidate.type !== THREAD_TITLE_CHUNK_TYPE) return null;
  const data = candidate.data;
  if (!data || typeof data !== 'object') return null;
  const { threadId, title } = data as Partial<ThreadTitleEvent>;
  if (typeof threadId !== 'string' || !threadId) return null;
  if (typeof title !== 'string' || !title.trim()) return null;
  return { threadId, title: title.trim() };
}

/**
 * Remove title parts from a chunk stream, reporting each through `onTitle`.
 * A callback failure never breaks the answer stream.
 */
export function observeThreadTitleChunks(
  stream: ReadableStream<UIMessageChunk>,
  onTitle: (event: ThreadTitleEvent) => void,
): ReadableStream<UIMessageChunk> {
  return stream.pipeThrough(
    new TransformStream<UIMessageChunk, UIMessageChunk>({
      transform(chunk, controller) {
        const event = threadTitleEvent(chunk);
        if (!event) {
          controller.enqueue(chunk);
          return;
        }
        try {
          onTitle(event);
        } catch {
          /* the title is a courtesy; the answer is not */
        }
      },
    }),
  );
}

type SendOptions = Parameters<AssistantChatTransport<UIMessage>['sendMessages']>[0];
type ReconnectOptions = Parameters<AssistantChatTransport<UIMessage>['reconnectToStream']>[0];
type BaseInitOptions = NonNullable<
  ConstructorParameters<typeof AssistantChatTransport<UIMessage>>[0]
>;

export type ThreadTitleTransportInitOptions = BaseInitOptions & {
  onThreadTitle: (event: ThreadTitleEvent) => void;
  resumeCancellation?: ReturnType<typeof createResumeCancellation>;
};

/**
 * The app's chat transport with the title part lifted off both the send and
 * the resume stream. Everything else (resumable ids, resilient fetch, headers)
 * is the base transport's, untouched.
 */
export class ThreadTitleTransport extends AssistantChatTransport<UIMessage> {
  private readonly onThreadTitle: (event: ThreadTitleEvent) => void;

  private readonly resumeCancellation?: ReturnType<typeof createResumeCancellation>;

  constructor({ onThreadTitle, resumeCancellation, ...options }: ThreadTitleTransportInitOptions) {
    super(options);
    this.onThreadTitle = onThreadTitle;
    this.resumeCancellation = resumeCancellation;
  }

  override async sendMessages(options: SendOptions): Promise<ReadableStream<UIMessageChunk>> {
    const stream = this.resumeCancellation
      ? await this.resumeCancellation.request(
          (abortSignal) => super.sendMessages({ ...options, abortSignal }),
          options.abortSignal,
        )
      : await super.sendMessages(options);
    return observeThreadTitleChunks(stream, this.onThreadTitle);
  }

  override async reconnectToStream(
    options: ReconnectOptions,
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    const stream = await super.reconnectToStream(options);
    if (!stream) return stream;
    return observeThreadTitleChunks(
      this.resumeCancellation?.wrap(stream) ?? stream,
      this.onThreadTitle,
    );
  }
}
